import { useState, useEffect, useRef } from "react";
import { useCalculatorStore } from "../store/calculatorStore";
import {
  useSharingStore,
  ShareMode,
  SharePermission,
  SharedUser,
} from "../store/sharingStore";
import { useAuth } from "../auth/AuthProvider";

const PERMISSION_LABELS: Record<SharePermission, string> = {
  manage: "Manager",
  write: "Editor",
  readcopy: "Commenter",
  read: "Viewer",
};

export function ShareDialog() {
  const { sessions } = useCalculatorStore();
  const {
    shareDialogSessionId,
    closeShareDialog,
    shareConfigs,
    fetchShareConfig,
    updateShareMode,
    addSharedUser,
    updateUserPermission,
    removeSharedUser,
    removeSharing,
    loadingShares,
  } = useSharingStore();
  const { getAccessToken, isAuthenticated, login } = useAuth();

  const [emailInput, setEmailInput] = useState("");
  const [selectedPermission, setSelectedPermission] =
    useState<SharePermission>("write");
  const [addError, setAddError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  const session = sessions.find((s) => s.id === shareDialogSessionId);
  const shareConfig = shareDialogSessionId
    ? shareConfigs[shareDialogSessionId]
    : null;
  const isLoading = shareDialogSessionId
    ? loadingShares.has(shareDialogSessionId)
    : false;

  // Fetch share config when dialog opens
  useEffect(() => {
    if (shareDialogSessionId && isAuthenticated) {
      setFetchError(null);
      fetchShareConfig(shareDialogSessionId, getAccessToken).catch((err) => {
        const message = err?.message || "Failed to load sharing settings";
        // Show authentication-related errors with sign-in prompt
        if (
          message.includes("Not authenticated") ||
          message.includes("401") ||
          message.includes("Unauthorized")
        ) {
          setFetchError("Your session has expired. Please sign in again.");
        } else {
          setFetchError(message);
        }
      });
    }
  }, [shareDialogSessionId, isAuthenticated]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        closeShareDialog();
      }
    };
    if (shareDialogSessionId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [shareDialogSessionId]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeShareDialog();
    };
    if (shareDialogSessionId) {
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }
  }, [shareDialogSessionId]);

  if (!shareDialogSessionId || !session) return null;

  const handleAddUser = async () => {
    if (!emailInput.trim()) return;
    setAddError(null);
    setIsAddingUser(true);
    try {
      const result = await addSharedUser(
        shareDialogSessionId,
        emailInput.trim(),
        selectedPermission,
        getAccessToken,
      );
      if (result) {
        setEmailInput("");
      } else {
        setAddError("User not found or could not be added");
      }
    } catch (err: any) {
      setAddError(err.message || "Failed to add user");
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleUpdateGeneralAccess = async (
    mode: ShareMode,
    permission: SharePermission,
  ) => {
    try {
      await updateShareMode(
        shareDialogSessionId,
        mode,
        permission,
        getAccessToken,
      );
    } catch (err) {
      console.error("Failed to update share mode:", err);
    }
  };

  const handleCopyLink = async () => {
    if (shareConfig?.shareUrl) {
      try {
        await navigator.clipboard.writeText(shareConfig.shareUrl);
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
      } catch {
        // Fallback
        const input = document.createElement("input");
        input.value = shareConfig.shareUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
      }
    }
  };

  const handleRemoveUser = async (userId: number) => {
    await removeSharedUser(shareDialogSessionId, userId, getAccessToken);
  };

  const handleUpdatePermission = async (
    userId: number,
    permission: SharePermission,
  ) => {
    await updateUserPermission(
      shareDialogSessionId,
      userId,
      permission,
      getAccessToken,
    );
  };

  const handleStopSharing = async () => {
    if (window.confirm("Stop sharing this session with everyone?")) {
      await removeSharing(shareDialogSessionId, getAccessToken);
    }
  };

  const currentShareMode = shareConfig?.shareMode || "private";
  const currentDefaultPermission = shareConfig?.defaultPermission || "read";

  return (
    <div className="share-dialog-overlay">
      <div className="share-dialog" ref={dialogRef}>
        {/* Header */}
        <div className="share-dialog-header">
          <h2>Share "{session.name}"</h2>
          <button className="share-dialog-close" onClick={closeShareDialog}>
            <CloseIcon />
          </button>
        </div>

        {isLoading ? (
          <div className="share-dialog-loading">
            Loading sharing settings...
          </div>
        ) : fetchError ? (
          <div className="share-dialog-section">
            <div className="share-dialog-error">
              <p>{fetchError}</p>
              <p
                style={{
                  marginTop: "12px",
                  fontSize: "0.9rem",
                  color: "var(--text-muted)",
                }}
              >
                Your session may have expired. Please try signing in again.
              </p>
              <button
                className="share-done-btn"
                onClick={() => {
                  if (isSigningIn) return;
                  setIsSigningIn(true);
                  login()
                    .then(() => {
                      setFetchError(null);
                      if (shareDialogSessionId) {
                        fetchShareConfig(shareDialogSessionId, getAccessToken);
                      }
                    })
                    .catch(() => {
                      // Login failed, error already logged in AuthProvider
                    })
                    .finally(() => {
                      setIsSigningIn(false);
                    });
                }}
                disabled={isSigningIn}
                style={{ marginTop: "12px" }}
              >
                {isSigningIn ? "Signing in..." : "Sign in"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Add people section */}
            <div className="share-dialog-section">
              <div className="share-add-people">
                <div className="share-add-input-row">
                  <input
                    type="email"
                    placeholder="Add people by email"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      setAddError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddUser();
                    }}
                    className="share-email-input"
                  />
                  <select
                    value={selectedPermission}
                    onChange={(e) =>
                      setSelectedPermission(e.target.value as SharePermission)
                    }
                    className="share-permission-select"
                  >
                    {(Object.keys(PERMISSION_LABELS) as SharePermission[]).map(
                      (perm) => (
                        <option key={perm} value={perm}>
                          {PERMISSION_LABELS[perm]}
                        </option>
                      ),
                    )}
                  </select>
                  <button
                    className="share-add-btn"
                    onClick={handleAddUser}
                    disabled={!emailInput.trim() || isAddingUser}
                  >
                    {isAddingUser ? "Adding..." : "Add"}
                  </button>
                </div>
                {addError && <div className="share-add-error">{addError}</div>}
              </div>
            </div>

            {/* People with access */}
            <div className="share-dialog-section">
              <h3 className="share-section-title">People with access</h3>
              <div className="share-people-list">
                {/* Owner */}
                <div className="share-person-row owner">
                  <div className="share-person-avatar">
                    <OwnerIcon />
                  </div>
                  <div className="share-person-info">
                    <span className="share-person-name">You (Owner)</span>
                  </div>
                  <span className="share-person-role">Owner</span>
                </div>

                {/* Shared users */}
                {shareConfig?.users.map((user) => (
                  <SharedUserRow
                    key={user.userId}
                    user={user}
                    onUpdatePermission={(perm) =>
                      handleUpdatePermission(user.userId, perm)
                    }
                    onRemove={() => handleRemoveUser(user.userId)}
                  />
                ))}

                {(!shareConfig?.users || shareConfig.users.length === 0) && (
                  <div className="share-people-empty">
                    No one else has access yet
                  </div>
                )}
              </div>
            </div>

            {/* General access */}
            <div className="share-dialog-section">
              <h3 className="share-section-title">General access</h3>
              <div className="share-general-access">
                <div className="share-access-icon">
                  {currentShareMode === "private" ? (
                    <LockIcon />
                  ) : currentShareMode === "account" ? (
                    <AccountIcon />
                  ) : (
                    <GlobeIcon />
                  )}
                </div>
                <div className="share-access-control">
                  <select
                    value={currentShareMode}
                    onChange={(e) =>
                      handleUpdateGeneralAccess(
                        e.target.value as ShareMode,
                        currentDefaultPermission,
                      )
                    }
                    className="share-access-mode-select"
                  >
                    <option value="private">Restricted</option>
                    <option value="account">Octagonl account holders</option>
                    <option value="public">Anyone with the link</option>
                  </select>
                  <span className="share-access-description">
                    {currentShareMode === "private"
                      ? "Only people added above can access"
                      : currentShareMode === "account"
                        ? "Anyone with an Octagonl account"
                        : "Anyone on the internet with the link"}
                  </span>
                </div>
                {currentShareMode !== "private" && (
                  <select
                    value={currentDefaultPermission}
                    onChange={(e) =>
                      handleUpdateGeneralAccess(
                        currentShareMode,
                        e.target.value as SharePermission,
                      )
                    }
                    className="share-default-permission-select"
                  >
                    <option value="read">{PERMISSION_LABELS.read}</option>
                    <option value="readcopy">
                      {PERMISSION_LABELS.readcopy}
                    </option>
                    <option value="write">{PERMISSION_LABELS.write}</option>
                  </select>
                )}
              </div>
            </div>

            {/* Footer with copy link and done */}
            <div className="share-dialog-footer">
              {currentShareMode !== "private" && (
                <button
                  className="share-copy-link-btn"
                  onClick={handleCopyLink}
                >
                  <LinkIcon />
                  {copyFeedback ? "Link copied!" : "Copy link"}
                </button>
              )}
              {currentShareMode !== "private" && (
                <button className="share-stop-btn" onClick={handleStopSharing}>
                  Stop sharing
                </button>
              )}
              <button className="share-done-btn" onClick={closeShareDialog}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Sub-component for each shared user row
function SharedUserRow({
  user,
  onUpdatePermission,
  onRemove,
}: {
  user: SharedUser;
  onUpdatePermission: (permission: SharePermission) => void;
  onRemove: () => void;
}) {
  return (
    <div className="share-person-row">
      <div className="share-person-avatar">
        <PersonIcon />
      </div>
      <div className="share-person-info">
        <span className="share-person-name">
          {user.displayName || user.email}
        </span>
        <span className="share-person-email">{user.email}</span>
      </div>
      <select
        value={user.permission}
        onChange={(e) =>
          e.target.value === "remove"
            ? onRemove()
            : onUpdatePermission(e.target.value as SharePermission)
        }
        className="share-person-permission-select"
      >
        {(Object.keys(PERMISSION_LABELS) as SharePermission[]).map((perm) => (
          <option key={perm} value={perm}>
            {PERMISSION_LABELS[perm]}
          </option>
        ))}
        <option value="remove">Remove access</option>
      </select>
    </div>
  );
}

// Share button component for use in SessionTabs and HistoryPanel
export function ShareButton({ sessionId }: { sessionId: string }) {
  const { openShareDialog } = useSharingStore();
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) return null;

  return (
    <button
      className="share-btn"
      onClick={(e) => {
        e.stopPropagation();
        openShareDialog(sessionId);
      }}
      title="Share this session"
    >
      <ShareIcon />
      <span className="share-btn-label">Share</span>
    </button>
  );
}

// Icons

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function AccountIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function OwnerIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
